import { ScrollingModule } from '@angular/cdk/scrolling';
import { DecimalPipe } from '@angular/common';
import { NgModule, inject } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconRegistry } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { DomSanitizer } from '@angular/platform-browser';
import { AgrAdvisorRelationshipsSelectorModule } from '@next/core-lib/advisor-relationships-selector';
import { AgrValueRowModule } from '@next/core-lib/components';
import { AgrDialogModule } from '@next/core-lib/dialog';
import { AgrSharedModule } from '@next/core-lib/shared';

import { CropFieldDataComponent } from './components/cropfield-data/cropfield-data.component';
import { SelectCustomerDialogComponent } from './components/select-customer-dialog/select-customer-dialog.component';
import { SoilAnalysisDetailComponent } from './components/soil-analysis-detail/soil-analysis-detail.component';
import { AgrStickyComponent } from './components/sticky/sticky.component';
import { AgrStickyDirective } from './directives/sticky/sticky.directive';

@NgModule({
  declarations: [
    // add shared components here
    AgrStickyDirective,
    AgrStickyComponent,
    CropFieldDataComponent,
    SoilAnalysisDetailComponent,
    SelectCustomerDialogComponent,
  ],
  imports: [
    ScrollingModule,
    AgrSharedModule,
    MatExpansionModule,
    AgrValueRowModule,
    AgrAdvisorRelationshipsSelectorModule,
    AgrDialogModule,
    MatTableModule,
  ],
  exports: [
    AgrSharedModule,
    AgrStickyComponent,
    MatSlideToggleModule,
    CropFieldDataComponent,
    SoilAnalysisDetailComponent,
  ],
  providers: [DecimalPipe],
})
export class SharedModule {
  constructor() {
    const matIcon = inject(MatIconRegistry);
    const sanitizer = inject(DomSanitizer);

    matIcon.addSvgIconLiteral(
      'vector_polyline_edit',
      sanitizer.bypassSecurityTrustHtml(
        /* eslint-disable-next-line max-len */
        `<svg style="width:24px;height:24px" viewBox="0 0 24 24"><path d="M2 3V9H4.95L6.95 15H6V21H12V16.41L17.41 11H22V5H16V9.57L10.59 15H9.06L7.06 9H8V3H2M4 5H6V7H4V5M18 7H20V9H18V7M21.15 13C21 13 20.87 13.05 20.76 13.16L19.75 14.18L21.82 16.25L22.84 15.24C23.05 15.03 23.05 14.67 22.84 14.46L21.54 13.16C21.43 13.05 21.29 13 21.15 13M19.15 14.76L13 20.91V23H15.09L21.23 16.84L19.15 14.76M8 17H10V19H8V17Z" /></svg>`,
      ),
    );
    matIcon.addSvgIconLiteral(
      'vector_polyline',
      sanitizer.bypassSecurityTrustHtml(
        /* eslint-disable-next-line max-len */
        `<svg style="width:24px;height:24px" viewBox="0 0 24 24"><path d="M2 3V9H4.95L6.95 15H6V21H12V16.41L17.41 11H22V5H16V9.57L10.59 15H9.06L7.06 9H8V3M4 5H6V7H4M18 7H20V9H18M8 17H10V19H8Z" /></svg>`,
      ),
    );
    matIcon.addSvgIconLiteral(
      'vector_polyline_minus',
      sanitizer.bypassSecurityTrustHtml(
        /* eslint-disable-next-line max-len */
        `<svg style="width:24px;height:24px" viewBox="0 0 24 24"><path d="M2 3V9H4.95L6.95 15H6V21H12V16.41L17.41 11H22V5H16V9.57L10.59 15H9.06L7.06 9H8V3H2M4 5H6V7H4V5M18 7H20V9H18V7M8 17H10V19H8V17M15 18V20H23V18H15Z" /></svg>`,
      ),
    );
    matIcon.addSvgIconLiteral(
      'vector_polyline_plus',
      sanitizer.bypassSecurityTrustHtml(
        /* eslint-disable-next-line max-len */
        `<svg style="width:24px;height:24px" viewBox="0 0 24 24"><path d="M2 3V9H4.95L6.95 15H6V21H12V16.41L17.41 11H22V5H16V9.57L10.59 15H9.06L7.06 9H8V3H2M4 5H6V7H4V5M18 7H20V9H18V7M18 15V18H15V20H18V23H20V20H23V18H20V15H18M8 17H10V19H8V17Z" /></svg>`,
      ),
    );
    matIcon.addSvgIconLiteral(
      'cruelty_free',
      sanitizer.bypassSecurityTrustHtml(
        /* eslint-disable-next-line max-len */
        `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M16.84 14.52c-.26-.19-.62-.63-.79-.84C17.24 12.01 19 8.87 19 5c0-1.95-.74-3-2-3-1.54 0-3.96 2.06-5 5.97C10.96 4.06 8.54 2 7 2 5.74 2 5 3.05 5 5c0 3.87 1.76 7.01 2.95 8.68-.17.21-.53.65-.79.84-.5.41-1.66 1.37-1.66 2.98 0 2.21 1.79 4 4 4 1.55 0 2.5-.56 2.5-.56s.95.56 2.5.56c2.21 0 4-1.79 4-4 0-1.61-1.16-2.57-1.66-2.98zM9.35 12.2C8.34 10.7 7 8.12 7 5c0-.49.06-.8.12-.97.94.31 3.24 2.71 3.38 7.64-.47.12-.84.3-1.15.53zm1.15 4.55c-.28 0-.5-.34-.5-.75s.22-.75.5-.75.5.34.5.75-.22.75-.5.75zM12 19.5c-.55 0-1-.72-1-1s.45-1 1-1 1 .22 1 .5-.45 1-1 1zm1.5-2.75c-.28 0-.5-.34-.5-.75s.22-.75.5-.75.5.34.5.75-.22.75-.5.75zm0-5.08c.14-4.93 2.44-7.33 3.38-7.64.06.17.12.48.12.97 0 3.12-1.34 5.7-2.35 7.2-.31-.23-.68-.41-1.15-.53z" /></svg>`,
      ),
    );
  }
}
